import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';

const RoleListLayer = () => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Add modal state
    const [showAddModal, setShowAddModal] = useState(false);
    const [addFormData, setAddFormData] = useState({
        name: '',
        slug: '',
        description: '',
        is_active: true,
    });
    const [addError, setAddError] = useState('');
    const [addValidationErrors, setAddValidationErrors] = useState({});
    const [isCreating, setIsCreating] = useState(false);

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [editError, setEditError] = useState('');
    const [editValidationErrors, setEditValidationErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/roles`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Unauthorized. Only super admin can access this page.');
                }
                throw new Error(`Failed to fetch roles: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setRoles(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch roles');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = () => {
        setAddFormData({
            name: '',
            slug: '',
            description: '',
            is_active: true,
        });
        setAddError('');
        setAddValidationErrors({});
        setShowAddModal(true);
    };

    const handleAddInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setAddFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Auto-generate slug from name
        if (name === 'name') {
            const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            setAddFormData(prev => ({
                ...prev,
                slug: slug
            }));
        }
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        setAddError('');
        setAddValidationErrors({});

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/roles`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(addFormData),
            });

            const data = await response.json();

            if (data.success) {
                setShowAddModal(false);
                fetchRoles();
            } else {
                if (data.errors) {
                    setAddValidationErrors(data.errors);
                } else {
                    setAddError(data.message || 'Failed to create role');
                }
            }
        } catch (err) {
            console.error(err);
            setAddError('Failed to create role. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const handleEditClick = (role) => {
        setEditingRole(role);
        setEditFormData({
            name: role.name,
            slug: role.slug,
            description: role.description || '',
            is_active: role.is_active,
        });
        setEditError('');
        setEditValidationErrors({});
        setShowEditModal(true);
    };

    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setIsUpdating(true);
        setEditError('');
        setEditValidationErrors({});

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/roles/${editingRole.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(editFormData),
            });

            const data = await response.json();

            if (data.success) {
                setShowEditModal(false);
                fetchRoles();
            } else {
                if (data.errors) {
                    setEditValidationErrors(data.errors);
                } else {
                    setEditError(data.message || 'Failed to update role');
                }
            }
        } catch (err) {
            console.error(err);
            setEditError('Failed to update role. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
            return;
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/roles/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                fetchRoles();
            } else {
                alert(data.message || 'Failed to delete role');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to delete role. Please try again.');
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex align-items-center justify-content-between">
                    <h6 className="text-lg fw-semibold mb-0">Role Management</h6>
                    <button
                        type="button"
                        onClick={handleAddClick}
                        className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                    >
                        <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
                        Add New Role
                    </button>
                </div>
            </div>

            <div className="card-body p-24">
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">S.L</th>
                                    <th scope="col">Role Name</th>
                                    <th scope="col">Slug</th>
                                    <th scope="col">Description</th>
                                    <th scope="col" className="text-center">Status</th>
                                    <th scope="col" className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {roles.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-4">
                                            No roles found
                                        </td>
                                    </tr>
                                ) : (
                                    roles.map((role, index) => (
                                        <tr key={role.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <span className="fw-semibold text-primary-600">
                                                    {role.name}
                                                </span>
                                            </td>
                                            <td>
                                                <code className="text-sm">{role.slug}</code>
                                            </td>
                                            <td>
                                                <span className="text-secondary-light">
                                                    {role.description || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <span
                                                    className={`px-24 py-4 radius-4 fw-medium text-sm ${
                                                        role.is_active
                                                            ? 'bg-success-focus text-success-600 border border-success-main'
                                                            : 'bg-danger-focus text-danger-600 border border-danger-main'
                                                    }`}
                                                >
                                                    {role.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center gap-10 justify-content-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditClick(role)}
                                                        className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    >
                                                        <Icon icon="lucide:edit" className="menu-icon" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(role.id)}
                                                        className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    >
                                                        <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add Role Modal */}
            {showAddModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add New Role</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowAddModal(false)}
                                    disabled={isCreating}
                                />
                            </div>
                            <form onSubmit={handleAddSubmit} noValidate>
                                <div className="modal-body">
                                    {addError && (
                                        <div className="alert alert-danger" role="alert">
                                            {addError}
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Role Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${addValidationErrors.name ? 'is-invalid' : ''}`}
                                            name="name"
                                            value={addFormData.name}
                                            onChange={handleAddInputChange}
                                            placeholder="e.g., Admin, Manager, User"
                                            required
                                        />
                                        {addValidationErrors.name && (
                                            <div className="invalid-feedback d-block">
                                                {addValidationErrors.name[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Slug <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${addValidationErrors.slug ? 'is-invalid' : ''}`}
                                            name="slug"
                                            value={addFormData.slug}
                                            onChange={handleAddInputChange}
                                            placeholder="e.g., admin, manager, user"
                                            required
                                        />
                                        <small className="text-secondary-light">Auto-generated from name (lowercase, hyphens)</small>
                                        {addValidationErrors.slug && (
                                            <div className="invalid-feedback d-block">
                                                {addValidationErrors.slug[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Description</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={addFormData.description}
                                            onChange={handleAddInputChange}
                                            rows="3"
                                            placeholder="Enter role description"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="is_active"
                                                checked={addFormData.is_active}
                                                onChange={handleAddInputChange}
                                                id="addIsActive"
                                            />
                                            <label className="form-check-label" htmlFor="addIsActive">
                                                Active
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowAddModal(false)}
                                        disabled={isCreating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isCreating}
                                    >
                                        {isCreating ? 'Creating...' : 'Create Role'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Role Modal */}
            {showEditModal && editingRole && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Role</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={isUpdating}
                                />
                            </div>
                            <form onSubmit={handleEditSubmit} noValidate>
                                <div className="modal-body">
                                    {editError && (
                                        <div className="alert alert-danger" role="alert">
                                            {editError}
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Role Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${editValidationErrors.name ? 'is-invalid' : ''}`}
                                            name="name"
                                            value={editFormData.name}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                        {editValidationErrors.name && (
                                            <div className="invalid-feedback d-block">
                                                {editValidationErrors.name[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">
                                            Slug <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control ${editValidationErrors.slug ? 'is-invalid' : ''}`}
                                            name="slug"
                                            value={editFormData.slug}
                                            onChange={handleEditInputChange}
                                            required
                                        />
                                        {editValidationErrors.slug && (
                                            <div className="invalid-feedback d-block">
                                                {editValidationErrors.slug[0]}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Description</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={editFormData.description}
                                            onChange={handleEditInputChange}
                                            rows="3"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="is_active"
                                                checked={editFormData.is_active}
                                                onChange={handleEditInputChange}
                                                id="editIsActive"
                                            />
                                            <label className="form-check-label" htmlFor="editIsActive">
                                                Active
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowEditModal(false)}
                                        disabled={isUpdating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? 'Updating...' : 'Update Role'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleListLayer;

